import { client } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	type ChartConfig
} from "@/components/ui/chart";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BarChart3, Wifi } from "lucide-react";
import * as React from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	Line,
	LineChart,
	XAxis,
	YAxis,
} from "recharts";

export const Route = createFileRoute("/_protected/dashboard")({
	component: Dashboard,
});

function Dashboard() {
	/* =========================
		 ESTADOS DE FILTRO
		 ========================= */
	const [zonaSeleccionada, setZonaSeleccionada] = React.useState<
		string | undefined
	>();
	const [fromDate, setFromDate] = React.useState("");
	const [toDate, setToDate] = React.useState("");
	const [paginaActual, setPaginaActual] = React.useState(1);
	const [isDownloading, setIsDownloading] = React.useState(false);
	const [isConsulting, setIsConsulting] = React.useState(false);

	// "all" / "" no es un id de zona válido: el backend espera un número o undefined
	const zonaParam =
		zonaSeleccionada && zonaSeleccionada !== "all"
			? zonaSeleccionada
			: undefined;

	/* =========================
		 QUERY ZONAS
		 ========================= */
	const { data: zonas } = useQuery({
		queryKey: ["zonas"],
		queryFn: async () => {
			const res = await client.zones.$get();
			return res.json();
		},
	})

	/* =========================
		 QUERY DASHBOARD (MANUAL)
		 ========================= */
	const { data, refetch } = useQuery({
		queryKey: ["dashboard", zonaSeleccionada, fromDate, toDate],
		enabled: false,
		queryFn: async () => {
			const response = await client.graph.$post({
				json: {
					from: fromDate,
					to: toDate,
					zone: zonaParam,
					sample: 250,
				},
			})
			const result = await response.json();
			return Array.isArray(result) ? result : [];
		},
		initialData: [],
	})

	const { data: disponibilidad, refetch: calcularDisponibilidad } = useQuery({
		queryKey: ["disponibilidad", zonaSeleccionada, fromDate, toDate],
		queryFn: async () => {
			try {
				const response = await client.availability.$get({
					query: {
						from: fromDate,
						to: toDate,
						zone: zonaParam,
					},
				})

				const result = await response.json();
				return result ?? { availability: 0, uptimeDays: 0 };
			} catch (error) {
				return { availability: 0, uptimeDays: 0 };
			}
		},
		enabled: false,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
		initialData: { availability: 0, uptimeDays: 0 },
	})

	const handleConsultar = async () => {
		setIsConsulting(true);
		try {
			await Promise.all([refetch(), calcularDisponibilidad()]);
		} catch (error) {
			console.error('Error al consultar:', error);
			alert('Error al realizar la consulta');
		} finally {
			setIsConsulting(false);
		}
	}

	const descargarReporte = async () => {
		setIsDownloading(true);
		try {
			const response = await client.generate_report.$get({
				query: {
					from: fromDate,
					to: toDate,
					zone: zonaParam,
				},
			})

			const blob = await response.blob()
			const url = window.URL.createObjectURL(blob)

			const a = document.createElement('a')
			a.href = url
			a.download = 'report.xlsx'
			document.body.appendChild(a)
			a.click()

			a.remove()
			window.URL.revokeObjectURL(url)
		} catch (error) {
			console.log(error)
			alert('Error al generar el reporte')
		} finally {
			setIsDownloading(false);
		}
	}



	/* =========================
		 PAGINACIÓN
		 ========================= */
	const ITEMS_POR_PAGINA = 20;
	const totalPaginas = Math.ceil(data.length / ITEMS_POR_PAGINA);

	const dataPaginada = React.useMemo(() => {
		const inicio = (paginaActual - 1) * ITEMS_POR_PAGINA;
		const fin = inicio + ITEMS_POR_PAGINA;
		return data.slice(inicio, fin);
	}, [data, paginaActual]);

	React.useEffect(() => {
		setPaginaActual(1);
	}, [zonaSeleccionada]);

	/* =========================
		 MÉTRICAS
		 ========================= */
	const pingPromedio =
		data.length > 0
			? Math.round(
				data.reduce((acc: number, z: any) => acc + (z.ping || 0), 0) /
				data.length,
			)
			: 0

	const zona = zonas?.find((z: any) => String(z.id) === zonaSeleccionada)

	return (
		<div className="flex min-h-screen bg-slate-100">
			{/* CONTENIDO */}
			<main className="w-full p-6 space-y-6">
				{/* HEADER */}
				<div>
					<h1 className="text-2xl font-bold">Dashboard</h1>
					<p className="text-gray-500">Monitoreo de zonas WiFi</p>
				</div>

				{/* FILTROS */}
				<Card>
					<CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-6">
						<div>
							<Label>Desde</Label>
							<Input
								type="date"
								value={fromDate}
								onChange={(e) => setFromDate(e.target.value)}
							/>
						</div>
						<div>
							<Label>Hasta</Label>
							<Input
								type="date"
								value={toDate}
								onChange={(e) => setToDate(e.target.value)}
							/>
						</div>
						<div>
							<Label>Zona</Label>
							<Select
								value={zonaSeleccionada}
								onValueChange={setZonaSeleccionada}
							>
								<SelectTrigger>
									<SelectValue placeholder="Seleccione zona" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">Todas las zonas</SelectItem>
									{Array.isArray(zonas) &&
										zonas.map((z) => (
											<SelectItem key={z.id} value={String(z.id)}>
												{z.municipio}-{z.name}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
						</div>
						{/* Contenedor para ambos botones */}
						<div className="flex items-end gap-2">
							<Button
								className="flex-1 cursor-pointer"
								onClick={handleConsultar}
								disabled={!fromDate || !toDate || isConsulting}
							>
								{isConsulting ? 'Consultando...' : 'Consultar'}
							</Button>
							<Button
								className="flex-1 bg-green-600 hover:bg-green-700 cursor-pointer"
								onClick={descargarReporte}
								disabled={!fromDate || !toDate || data.length === 0 || isDownloading}
							>
								{isDownloading ? 'Generando...' : 'Generar Reporte'}
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* INDICADOR DE CARGA */}
				{isConsulting && (
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
						<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
						<span className="text-blue-700 font-medium">Cargando datos...</span>
					</div>
				)}

				{/* INDICADOR DE DESCARGA */}
				{isDownloading && (
					<div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
						<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
						<span className="text-green-700 font-medium">Generando reporte...</span>
					</div>
				)}

				{/* CARDS */}
				<section className="grid grid-cols-1 md:grid-cols-4 gap-4">
					<StatsCard title="Disponibilidad" value={`${disponibilidad.availability.toFixed(2)}%`} />
					<StatsCard title="Días Activa" value={`${disponibilidad.uptimeDays.toFixed(2)} días`} />
					<StatsCard title="Ping Promedio" value={`${pingPromedio} ms`} />
					<StatsCard
						title="Zona"
						value={
							zonaSeleccionada === "all"
								? "Todas"
								: zona ? `${zona.code}-${zona.municipio}-${zona.name}` : "—"
						}
					/>
				</section>

				{/* GRÁFICAS */}
				<section className="grid grid-cols-1 gap-6">
					<LineChartCard data={data} />
					<AreaChartCard data={data} />
				</section>
			</main>
		</div>
	)
}

// Tooltip personalizado
const CustomTooltip = ({ active, payload }: {
	active?: boolean;
	payload?: any;
}) => {
	if (active && payload && payload.length) {
		return (
			<div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
				<p className="font-semibold text-sm mb-2">
					{payload[0].payload.fechaCompleta}
				</p>
				{payload.map((entry, index) => (
					<p key={index} className="text-sm" style={{ color: entry.color }}>
						{entry.name}: {entry.value.toFixed(2)}
					</p>
				))}
			</div>
		)
	}
	return null;
}

/* =========================
	 GRÁFICAS
	 ========================= */

// Cada día trae varios puntos, así que el eje X se etiqueta una sola vez por
// día.
function fechaCompleta(timestamp: number) {
	return new Date(timestamp).toLocaleString("es-CO", {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hour12: true
	});
}

function formatearDia(timestamp: number) {
	return new Date(timestamp).toLocaleDateString("es-CO", {
		day: '2-digit',
		month: '2-digit',
	});
}

// Devuelve el timestamp del primer punto de cada día: una etiqueta por día, sin saltos.
// El eje es de tipo categoría, así que el tick tiene que ser un valor que exista en los
// datos — de ahí que se tome el primer punto del día y no la medianoche.
function ticksPorDia(data: any[]) {
	const primerosDelDia: number[] = [];
	let ultimoDia = "";

	for (const z of data) {
		const dia = new Date(z.timestamp).toDateString();
		if (dia !== ultimoDia) {
			primerosDelDia.push(z.timestamp);
			ultimoDia = dia;
		}
	}

	return primerosDelDia;
}

function LineChartCard({ data }) {
	const chartData = data.map((z) => ({
		ts: z.timestamp,
		fechaCompleta: fechaCompleta(z.timestamp),
		ping: z.ping || 0,
	}));

	const ticks = React.useMemo(() => ticksPorDia(data), [data]);

	const config = {
		ping: { label: "Ping", color: "hsl(262, 83%, 58%)" },
	} satisfies ChartConfig;

	return (
		<Card>
			<CardHeader>
				<CardTitle>Latencia por Zona</CardTitle>
				<CardDescription>Ping en milisegundos</CardDescription>
			</CardHeader>
			<CardContent>
				<ChartContainer config={config} className="h-75 w-full">
					<LineChart data={chartData}>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis
							dataKey="ts"
							ticks={ticks}
							tickFormatter={formatearDia}
							angle={-45}
							textAnchor="end"
							height={60}
							interval={0}
						/>
						<YAxis />
						<ChartTooltip content={<CustomTooltip />} />
						<Line
							dataKey="ping"
							stroke="var(--color-ping)"
							strokeWidth={2}
							dot={false}
						/>
					</LineChart>
				</ChartContainer>
			</CardContent>
		</Card>
	)
}

function AreaChartCard({ data }) {
	const chartData = data.map((z) => ({
		ts: z.timestamp,
		fechaCompleta: fechaCompleta(z.timestamp),
		download: z.download || 0,
		upload: z.upload || 0,
	}));

	const ticks = React.useMemo(() => ticksPorDia(data), [data]);

	const config = {
		download: { label: "Download", color: "hsl(217, 91%, 60%)" },
		upload: { label: "Upload", color: "hsl(142, 76%, 36%)" },
	} satisfies ChartConfig;

	return (
		<Card>
			<CardHeader>
				<CardTitle>Velocidad de Conexión</CardTitle>
				<CardDescription>Download y Upload (Mbps)</CardDescription>
			</CardHeader>
			<CardContent>
				<ChartContainer config={config} className="h-90 w-full">
					<AreaChart data={chartData}>
						<CartesianGrid vertical={false} />
						<XAxis
							dataKey="ts"
							ticks={ticks}
							tickFormatter={formatearDia}
							angle={-45}
							textAnchor="end"
							height={60}
							interval={0}
						/>
						<ChartTooltip content={<CustomTooltip />} />
						<Area
							dataKey="upload"
							stackId="a"
							fill="var(--color-upload)"
							stroke="var(--color-upload)"
						/>
						<Area
							dataKey="download"
							stackId="a"
							fill="var(--color-download)"
							stroke="var(--color-download)"
						/>
						<ChartLegend content={<ChartLegendContent />} />
					</AreaChart>
				</ChartContainer>
			</CardContent>
		</Card>
	)
}

/* =========================
	 UI
	 ========================= */

function MenuItem({ icon: Icon, label, active }: any) {
	return (
		<div
			className={`flex items-center gap-3 p-2 rounded cursor-pointer ${active ? "bg-slate-800" : "hover:bg-slate-800"
				}`}
		>
			<Icon size={18} />
			{label}
		</div>
	)
}

function StatsCard({ title, value }: any) {
	return (
		<div className="bg-white p-4 rounded shadow">
			<div className="text-sm text-gray-500">{title}</div>
			<div className="text-3xl font-bold mt-2">{value}</div>
		</div>
	)
}

